import { useRouter } from "next/router";
import styled from "@emotion/styled";

import { Button } from "@shared/lib/components/common/atom/button";
import { BUTTON_TYPE } from "@shared/lib/styles/theme";
import { useCustomTranslation } from "@/utils/useCustomTranslation";

import { ClassContent } from "./ClassContent";
import { convertPurchaseStatus, PurchaseStatus } from "./convertPurchaseResult";
import { CustomerInfo } from "./CustomerInfo";
import { PointContent } from "./PointContent";
import { PriceContent } from "./PriceContent";
import { PurchaseType } from "./PurchaseType";
import { PaymentResponseDto } from "@shared/generated/api/model";

interface SuccessProps {
  PurchaseData: PaymentResponseDto;
}

export const SuccessContent = ({ PurchaseData }: SuccessProps) => {
  const { t } = useCustomTranslation();
  const router = useRouter();
  const { courseInfo, amountInfo, pointInfo, userInfo, purchaseResult } =
    (PurchaseData as PaymentResponseDto) || {};
  const { language } = courseInfo;
  const isKo = language === "ko";

  const handleBackPurchase = () => {
    router.push(`/my/orders/${purchaseResult?.purchaseId}`);
  };

  return (
    <>
      <SuccessTitle>
        {purchaseResult?.purchaseStatus &&
          t(
            convertPurchaseStatus(
              purchaseResult?.purchaseStatus as PurchaseStatus,
            ),
          )}
      </SuccessTitle>
      <CardWrapper>
        {courseInfo && (
          <ClassContent
            classData={courseInfo}
            paymentStatus={true}
          />
        )}
        {courseInfo && (
          <PriceContent
            language={language}
            usedPoints={amountInfo?.usedPoints || 0}
            price={amountInfo?.amount || 0}
            discountRate={amountInfo?.discountRate || 0}
            paymentStatus={true}
          />
        )}
        {isKo && (
          <PointContent
            earnedPoint={pointInfo?.earnedPoint}
            usedPoints={pointInfo?.usedPoints}
          />
        )}
        {userInfo && <CustomerInfo customerData={userInfo} />}
        <PurchaseType
          isKo={isKo}
          purchaseResult={purchaseResult}
        />
        <TermsSection>
          <Description className="bold">
            {t("important_notice_for_live_classes")}
          </Description>
          <Description>
            {t("live_class_notice").replaceAll("\\n", "\n")}
          </Description>
        </TermsSection>
        <LinkButton
          type={"submit"}
          styleType={BUTTON_TYPE.OUTLINE}
          onClick={handleBackPurchase}
          disabled={false}
        >
          {t("check_purchase_history")}
        </LinkButton>
      </CardWrapper>
    </>
  );
};

const SuccessTitle = styled.div`
  ${({ theme }) => theme.typography.h1.b};
  color: ${({ theme }) => theme.colors.text.high.white};
  padding-bottom: 40px;
`;

const CardWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
`;

const TermsSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  .bold {
    font-weight: 700;
  }
`;

const Description = styled.div`
  ${({ theme }) => theme.typography.body2.m};
  color: ${({ theme }) => theme.colors.text.medium.white};
  white-space: pre-line;
`;

const LinkButton = styled(Button)`
  &.btn-${BUTTON_TYPE.OUTLINE.toLowerCase()} {
    ${({ theme }) => theme.typography.body1.b};
    width: 100%;
    border-radius: 8px;
    padding: 12px 16px;
    /* margin-top: 24px; */
    border: 1px solid ${({ theme }) => theme.colors.icon.inactive.white};
    color: ${({ theme }) => theme.colors.text.high.white};

    &:focus {
      background-color: ${({ theme }) => theme.colors.brand.primary.hover};
    }
    &:hover {
      background-color: ${({ theme }) => theme.colors.brand.primary.pressed};
    }

    &:disabled {
      background-color: ${({ theme }) => theme.colors.brand.white.disabled};
      color: ${({ theme }) => theme.colors.text.disabled.white};
    }
  }
`;
